import { type FC } from 'react';
import { type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type BaseProps = {
  onClick?: () => void;
  icon: LucideIcon;
  dataCy?: string;
  disabled?: boolean;
  tooltip?: string | null;
  iconClassName?: string;
};

// Either `title` or `label` must be provided
type WithTitle = { title: string; label?: string };
type WithLabel = { title?: string; label: string };

export type ToolbarButtonProps = BaseProps & (WithTitle | WithLabel);

export const ToolbarButton: FC<ToolbarButtonProps> = ({
  onClick,
  icon: Icon,
  label,
  title,
  dataCy,
  disabled,
  tooltip,
  iconClassName,
}) => {
  // Fallback value for title if needed
  const buttonTitle = title ?? label;
  // Use the available title or label to construct the data-cy attribute if dataCy property is not provided
  const dataCyValue = dataCy ?? `toolbar-${buttonTitle?.split(' ').join('-').toLowerCase()}-button`;

  return (
    <Button
      variant="main-toolbar"
      size="sm"
      className={cn('group/toolbar-button', { 'pl-2': Icon })}
      onClick={() => onClick?.()}
      aria-label={buttonTitle}
      data-cy={dataCyValue}
      disabled={disabled}
      icon={Icon}
      iconClassName={cn(
        'transition-colors duration-200 icon-[18px] stroke-icon-base group-hover/toolbar-button:stroke-text-base',
        iconClassName,
      )}
      tooltip={tooltip ?? undefined}
      tooltipClassName="max-w-72"
    >
      {label && <div className="text-xs whitespace-nowrap text-text-base">{label}</div>}
    </Button>
  );
};
